import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { EndPointApi } from '@helpers/endpointapi';
import { AuthService } from './auth.service';
import { PublishRequestStatus } from 'icreate/common/models/approval-request';
import { AccessRequestStatus } from 'icreate/common/models/access-request';
import { RuntimeEnvironments } from '../models/runtime-enviroment.enum';
import { EnvironmentService } from './environment.service';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private domain: string;
    private currentUser: { name: string; oid: string; email: string };
    private env: RuntimeEnvironments;

    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private environmentService: EnvironmentService
    ) {
        this.domain = EndPointApi.iApiDomain();
        this.env = this.environmentService.getEnvironment();
    }

    async getAll(userOid) {
        if (this.env == RuntimeEnvironments.ELECTRON) return [];
        const notifications = await lastValueFrom<any[]>(
            this.http.get<any[]>(`${this.domain}/api/notifications/${userOid}`)
        );
        return notifications;
    }

    async updateAccessRequest(notificationId, messageRead, skipGuard = false) {
        return lastValueFrom(
            this.http.put(`${this.domain}/api/notifications/accessRequest/${notificationId}`, {
                messageRead,
                skipGuard,
            })
        );
    }

    async updatePublishRequest(notificationId, messageRead) {
        return lastValueFrom(
            this.http.put(`${this.domain}/api/notifications/publish/${notificationId}`, {
                messageRead,
            })
        );
    }

    filterNotifications(notifications: any[]): any[] {
        this.currentUser = this.authService.getCurrentUser();
        const statuses = [
            AccessRequestStatus.Pending,
            AccessRequestStatus.Editing,
            AccessRequestStatus.Accepted,
            AccessRequestStatus.Rejected,
            AccessRequestStatus.Removed,
            PublishRequestStatus.Pending,
            PublishRequestStatus.Accepted,
            PublishRequestStatus.Rejected,
            PublishRequestStatus.Removed,
        ];

        const distinctNotifications = [...new Set(notifications)];

        return distinctNotifications
            .filter(notification => {
                return statuses.some(status => this.shouldShowNotification(notification, status));
            })
            .sort((a, b) => new Date(b?.updatedAt)?.getTime() - new Date(a?.updatedAt)?.getTime());
    }

    private shouldShowNotification(notification: any, status: any): boolean {
        if (!notification || notification.hidden) return false;

        if (notification.messageRead) return false;

        if (
            status === AccessRequestStatus.Pending ||
            status === AccessRequestStatus.Editing ||
            status === AccessRequestStatus.Accepted ||
            status === AccessRequestStatus.Rejected ||
            status === AccessRequestStatus.Removed
        ) {
            return this.isAccessNotification(notification, status);
        }

        if (
            status === PublishRequestStatus.Pending ||
            status === PublishRequestStatus.Accepted ||
            status === PublishRequestStatus.Rejected
        ) {
            return this.isPublishNotification(notification, status);
        }

        return false;
    }

    private isAccessNotification(notification: any, status: any): boolean {
        const userOidMatch =
            status === AccessRequestStatus.Pending || status === AccessRequestStatus.Editing
                ? notification.ownerOid === this.currentUser.oid
                : notification.userOid === this.currentUser.oid;

        return this.showNotification(notification, status) && userOidMatch;
    }

    private isPublishNotification(notification: any, status: any): boolean {
        const ownerOrApproverOidMatch =
            status === PublishRequestStatus.Pending
                ? notification.approverOid === this.currentUser.oid
                : notification.ownerOid === this.currentUser.oid;

        return this.showNotification(notification, status) && ownerOrApproverOidMatch;
    }

    private showNotification(notification: any, status: any): boolean {
        return notification && !notification.hidden && notification.requestStatus === status;
    }
}
